/**
 * Shared cardinality budget constants for the Scene Tree.
 *
 * Single source of truth per DEC-90-05 (RFC-118 §11.1-E).
 * Both the pre-flight gauge and the tree virtualiser read from this module.
 *
 * Locked limits per DEC-20-05 (20-two-tier-architecture.md §8.2):
 *   ≤ 10 000 Tier-1 nodes  (LoR 0–3, scope manifest)
 *   ≤ 100 000 Tier-2 nodes (one asset's sub-tree)
 *   ≤ 500 DOM-mounted rows  (virtualisation requirement)
 *   200 per-level cap       (Bentley iTwin–validated pattern)
 */

export const SCENE_TREE_BUDGETS = {
  MAX_TIER1_NODES: 10_000,
  MAX_TIER2_NODES: 100_000,
  MAX_DOM_ROWS: 500,
  MAX_PER_LEVEL: 200,
} as const;

/**
 * Evaluates whether the total node count is within, approaching, or
 * exceeding the Tier-2 node budget.
 *
 * - `"ok"`     — below 80 % of MAX_TIER2_NODES
 * - `"warn"`   — 80 %–99 % of MAX_TIER2_NODES  (surface a warning in UI)
 * - `"exceed"` — at or above 100 % of MAX_TIER2_NODES (block or truncate)
 */
export function evaluateBudget(
  totalNodes: number,
): "ok" | "warn" | "exceed" {
  const limit = SCENE_TREE_BUDGETS.MAX_TIER2_NODES;
  if (totalNodes >= limit) return "exceed";
  if (totalNodes >= limit * 0.8) return "warn";
  return "ok";
}
